import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { LayoutDashboard, Plus } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ApiError } from '@/api/client'
import { dashboardApi } from './api'
import { EDIT_FLAG, type Dashboard } from './model'

function NewDashboardDialog({ open, onOpenChange, onCreate }: {
    open: boolean
    onOpenChange: (o: boolean) => void
    onCreate: (d: { name: string; description: string; shared: 'private' | 'org' }) => Promise<void>
}) {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [org, setOrg] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => { if (open) { setName(''); setDescription(''); setOrg(false); setError('') } }, [open])

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true); setError('')
        try { await onCreate({ name: name.trim(), description, shared: org ? 'org' : 'private' }) }
        catch (err) {
            const a = err as ApiError
            setError([a.message, ...(a.problems ?? [])].join(' · '))
            setBusy(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <form onSubmit={submit} className="flex flex-col gap-4">
                    <DialogHeader>
                        <DialogTitle>New dashboard</DialogTitle>
                        <DialogDescription>Start with an empty canvas, then add widgets.</DialogDescription>
                    </DialogHeader>
                    <FieldGroup>
                        <Field data-invalid={error ? true : undefined}>
                            <FieldLabel htmlFor="d-name">Name</FieldLabel>
                            <Input id="d-name" autoFocus required maxLength={200} value={name} aria-invalid={error ? true : undefined} onChange={(e) => setName(e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="d-desc">Description</FieldLabel>
                            <Textarea id="d-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </Field>
                        <Field orientation="horizontal">
                            <Switch id="d-shared" checked={org} onCheckedChange={setOrg} />
                            <div className="flex flex-col gap-1">
                                <FieldLabel htmlFor="d-shared">Share with the organisation</FieldLabel>
                                <FieldDescription>Everyone can view it; each person still sees only data they have access to.</FieldDescription>
                            </div>
                        </Field>
                    </FieldGroup>
                    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={busy || !name.trim()}>{busy && <Spinner data-icon="inline-start" />}Create</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default function DashboardsPage() {
    const { userId } = useUserRole()
    const navigate = useNavigate()
    const [items, setItems] = useState<Dashboard[] | null>(null)
    const [error, setError] = useState('')
    const [dialog, setDialog] = useState(false)

    useEffect(() => {
        dashboardApi.list().then((l) => setItems([...l].sort((a, b) => Number(b.owner_id === 'system') - Number(a.owner_id === 'system')))).catch((e) => { setError((e as Error).message); setItems([]) })
    }, [])

    // Router has no search schema for this route, so hand "open in edit mode" over via sessionStorage.
    const open = (id: string) => {
        try { sessionStorage.setItem(EDIT_FLAG + id, '1') } catch { /* ignore */ }
        navigate({ to: '/dashboards/$id', params: { id } })
    }

    const create = async (d: { name: string; description: string; shared: 'private' | 'org' }) => {
        const created = await dashboardApi.create({ ...d, layout: { widgets: [] } })
        open(created.id)
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Dashboards</h1>
                    <p className="text-sm text-muted-foreground">Charts and tables over your forms' submissions.</p>
                </div>
                <Button onClick={() => setDialog(true)}><Plus data-icon="inline-start" />New dashboard</Button>
            </div>

            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

            {items === null ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-36" />)}
                </div>
            ) : items.length === 0 ? (
                <Empty>
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><LayoutDashboard /></EmptyMedia>
                        <EmptyTitle>No dashboards yet</EmptyTitle>
                        <EmptyDescription>Create a dashboard to chart submissions by status, severity, time and more.</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                        <Button onClick={() => setDialog(true)}><Plus data-icon="inline-start" />New dashboard</Button>
                    </EmptyContent>
                </Empty>
            ) : (
                <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((d) => (
                        <li key={d.id}>
                            <Card className="relative h-full transition-colors hover:bg-muted/40">
                                <CardHeader>
                                    <CardTitle>
                                        <Link to="/dashboards/$id" params={{ id: d.id }} className="after:absolute after:inset-0 hover:underline focus-visible:outline-none focus-visible:underline">
                                            {d.name}
                                        </Link>
                                    </CardTitle>
                                    {d.description && <CardDescription className="line-clamp-2">{d.description}</CardDescription>}
                                </CardHeader>
                                <CardFooter className="mt-auto flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    {d.owner_id === 'system'
                                        ? <Badge variant="secondary">Built in</Badge>
                                        : <Badge variant={d.shared === 'org' ? 'secondary' : 'outline'}>{d.shared === 'org' ? 'Shared' : 'Private'}</Badge>}
                                    {d.owner_id === userId && <Badge variant="outline">Owned by you</Badge>}
                                    <span>{d.layout?.widgets?.length ?? 0} widgets</span>
                                    <span className="ml-auto">Updated {new Date(d.updated_at).toLocaleDateString()}</span>
                                </CardFooter>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}

            <NewDashboardDialog open={dialog} onOpenChange={setDialog} onCreate={create} />
        </div>
    )
}
